import { ISchedulable } from "./schedulable.interface";
import { isFunction } from "util";
import { isDefined } from "ionic-angular/util/util";

export interface IScheduledTaskRequirements {
    online: boolean;
    authenticated: boolean;
}

export class ScheduledTask {

    protected lastRun: Date;

    protected dependencies: Array<ScheduledTask> = [];

    protected startTime: number = 0;

    constructor(protected id: string,
                protected service: ISchedulable,
                protected fnName: string,
                protected args: Array<any>,
                protected priority: number,
                protected interval: number,
                protected maxTime: number,
                protected dependencyIds: string | Array<string>,
                protected requirements: IScheduledTaskRequirements = {} as IScheduledTaskRequirements) {
    }

    getId(): string {
        return this.id;
    }

    getService(): ISchedulable {
        return this.service;
    }

    getFnName(): string {
        return this.fnName;
    }

    getArgs(): Array<any> {
        return this.args;
    }

    getPriority(): number {
        return this.priority;
    }

    getInterval(): number {
        return this.interval;
    }

    getDependencyIds(): string | Array<string> {
        return this.dependencyIds;
    }

    getDependencies(): Array<ScheduledTask> {
        return this.dependencies;
    }

    addDependency(dep: ScheduledTask): void {
        this.dependencies.push(dep);
    }

    isBusy(): boolean {
        try {
            return this.service.isBusy();
        } catch (e) {
            console.error(e);
            return false;
        }

    }

    timeout(): boolean {
        return (Date.now() - this.startTime) > (this.maxTime * 1000);
    }

    stopService(): void {
        this.service.stop();
    }

    dateToUnix(d: Date): number {
        return Math.round(d.getTime() / 1000);
    }

    getNow(): number {
        return this.dateToUnix(new Date());
    }

    setLastRun(d: Date): void {
        this.lastRun = d;
    }

    getTimeToNextRun(): number {
        const lr = this.lastRun ? this.dateToUnix(this.lastRun) : 0;
        const i = this.getInterval();
        const now = this.getNow();
        const result = (now - lr - i) * -1;

        if (result === -0) {
            return 0;
        }

        return result;
    }

    execute(): Promise<any> {
        this.startTime = Date.now();

        return new Promise((resolve, reject) => {

            if (!this.canRun()) {
                resolve();
                return;
            }

            if (!isFunction(this.service[this.getFnName()])) {
                throw Error(`${this.getFnName()} is not a function`);
            }

            this.service[this.getFnName()].apply(this.service, this.getArgs()).then(
                () => {
                    this.onAfterExecute();
                    resolve();
                },
                reject
            );
        });
    }

    onAfterExecute(): void {
        this.lastRun = new Date();
    }

    canRun(): boolean {

        let dependenciesBusy = false;

        if (this.isBusy()) {
            return false;
        }

        this.dependencies.forEach(d => {
            if (d.isBusy()) {
                dependenciesBusy = true;
            }
        });

        return !dependenciesBusy;
    }

    /**
     * @todo refactor this to use Session
     * @param isOnline
     * @param isAuthenticated
     * @return {boolean}
     */
    checkRequirements(isOnline, isAuthenticated): boolean {

        if (isDefined(this.requirements.online) && this.requirements.online !== isOnline) {
            return false;
        }

        if (isDefined(this.requirements.authenticated) && this.requirements.authenticated !== isAuthenticated) {
            return false;
        }

        return true;
    }
}



// WEBPACK FOOTER //
// ./src/core/services/scheduler/scheduled-task.ts